import React, { useState, useEffect, useRef } from "react";
import { Add, Close } from "@mui/icons-material";
import {
  Button,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  FormControl,
  Typography,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import { SingleSelectType } from "../../table/Filter";
import { useFormik } from "formik";
import { showErrorMsg } from "../../../utils/toast";
import * as yup from "yup";
import { startCase } from "lodash";
import {
  Log,
  getPaymentHostRelation,
  getPayoutDetails,
  getPayoutStatic,
  payoutAction,
} from "../../../services/services";
import { DatePicker, LocalizationProvider } from "@mui/lab";
import AdapterMoment from "@mui/lab/AdapterMoment";
import { Info } from "@mui/icons-material";
import FormDialog from "../../FormDialog";

const CycleTooltip = () => {
  const CustomTooltip = () => (
    <Box>
      <Typography>
        A - Tuesday and Friday <br />B - Monday, Wednesday and Friday <br />C -
        T+2 <br />D - Friday Only <br />E - Tuesday and Thursday <br />F - T+3{" "}
        <br />G - T+4 <br />H - T+5
      </Typography>
    </Box>
  );

  return (
    <Tooltip title={<CustomTooltip />} placement="right-end">
      <IconButton sx={{ mt: 2, ml: -1 }}>
        <Info />
      </IconButton>
    </Tooltip>
  );
};

function EditPayoutDialog({
  open,
  onClose,
  selectedId,
  setResponse,
  setLoaded,
}) {
  const formRef = useRef(null);
  const [paymentRelation, setPaymentRelation] = useState([]);
  const [payoutInfo, setPayoutInfo] = useState();
  const [staticFields, setStaticFields] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [initialValues, setInitialValues] = useState();

  useEffect(() => {
    getPaymentHostRelation(setPaymentRelation, setLoaded);
    getPayoutStatic(setStaticFields);
    getPayoutDetails(selectedId, setPayoutInfo, setLoaded);
  }, [selectedId]);

  useEffect(() => {
    if (payoutInfo) {
      setSelectedOptions({
        merchant: payoutInfo.merchant.id,
        annual_fee_period: payoutInfo.annual_fee_period,
        annual_fee_start_date: new Date(payoutInfo.annual_fee_start_date),
        payment_cycle: payoutInfo.payment_cycle.id,
        payment_host: payoutInfo.payment_host.id,
      });
      setInitialValues({
        annual_fee: payoutInfo.annual_fee,
        minimum_payout_amount: payoutInfo.minimum_payout_amount,
        fixed_amount: payoutInfo.fixed_amount,
        rolling_amount: payoutInfo.rolling_amount,
        ...payoutInfo.bank_details,
      });
    }
  }, [payoutInfo]);

  const validationSchema = yup.object({
    initialValues: {},
    email: yup
      .string("Enter your email")
      .email("Enter a valid email")
      .required("Email is required"),
    password: yup
      .string("Enter your password")
      .min(8, "Password should be of minimum 8 characters length")
      .required("Password is required"),
    merchant: yup.string().required(),
    payment_host: yup.string().required(),
    payment_cycle: yup.string().required(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...initialValues,
    },
    // validationSchema: validationSchema,
    validationSchema: yup.object().shape(
      {
        // merchant: yup.string().required("This field is required"),
        // payment_host: yup.string().required("This field is required"),
        // payment_cycle: yup.string().required("This field is required"),

        account_number: yup.string().required("This field is required"),
        bank: yup.string().required("This field is required"),
        bank_address: yup.string().required("This field is required"),
        bank_transfer_fee: yup.string().required("This field is required"),
        bic_swift_code: yup.string().when(["iban"], {
          is: (iban) => !iban,
          then: yup.string().required("At least one field is required"),
          otherwise: yup.string().optional().nullable(),
        }),
        iban: yup.string().when(["bic_swift_code"], {
          is: (a) => !a,
          then: yup.string().required("At least one field is required"),
          otherwise: yup.string().optional().nullable(),
        }),
      },
      [["iban", "bic_swift_code"]]
    ),
    onSubmit: (values, { setSubmitting }) => {
      let final = {
        ...values,
        ...selectedOptions,
        annual_fee_start_date: selectedOptions.annual_fee_start_date
          .toISOString()
          .slice(0, 10),
      };
      Log.info(`Click button [Submit] - Update Merchant Payout details by Id: ${selectedId}`, final)
      payoutAction("UPDATE", null, null, final, selectedId, onClose);
      setSubmitting(false);
    },
  });

  const bank_details = [
    { name: "account_number", sx: ``, required: true },
    { name: "bank", sx: ``, required: true },
    { name: "bank_address", sx: { width: "50ch!important" }, required: true },
    { name: "bic_swift_code", sx: ``, type: "number", required: true },
    { name: "iban", sx: ``, required: true },
    { name: "bank_transfer_fee", sx: ``, type: "number", required: true },
    { type: "divider" },
    { name: "beneficiary_name", sx: `` },
    { name: "beneficiary_bank_code", sx: `` },
    { name: "beneficiary_branch_code", sx: `` },
    { name: "beneficiary_address", sx: { width: "50ch!important" } },
    { type: "divider" },
    { name: "intermediary_bank", sx: `` },
    { name: "intermediary_bank_code", sx: `` },
    { name: "intermediary_name", sx: `` },
  ];

  const paymentHostOptions = () => {
    const options = [];
    if (selectedOptions.merchant !== "") {
      const found_id = paymentRelation.find(
        (e) => e.id === selectedOptions.merchant
      );
      if (found_id && found_id.terminal_merchant.length !== 0) {
        found_id.terminal_merchant[0].payment_mapping_terminal.forEach((host) =>
          options.push({
            name: host.payment_host.name,
            id: host.payment_host.id,
          })
        );
      }
    }
    return options;
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title="Edit Merchant Payout"
      handleSubmit={() => formRef.current?.requestSubmit()}
      loading={formik.isSubmitting}
    >
      <form ref={formRef} onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            "& .MuiFormControl-root": { m: 1, width: "25ch" },
          }}
        >
          <SingleSelectType
            id="true"
            options={paymentRelation.map((merchant) => ({
              name: merchant.name,
              id: merchant.id,
            }))}
            selected={selectedOptions}
            setSelected={setSelectedOptions}
            label="Merchant"
            param={"merchant"}
            required={true}
          />
          <SingleSelectType
            id="true"
            options={paymentHostOptions()}
            selected={selectedOptions}
            setSelected={setSelectedOptions}
            label="Payment Host"
            param={"payment_host"}
            required={true}
          />
          <SingleSelectType
            id="true"
            options={
              staticFields.cycle &&
              staticFields.cycle.map((cycle) => ({
                name: cycle.cycle,
                id: cycle.id,
              }))
            }
            selected={selectedOptions}
            setSelected={setSelectedOptions}
            label="Payment Cycle"
            param={"payment_cycle"}
            required={true}
          />
          <CycleTooltip />
          <Divider sx={{ my: 1 }} />
          <TextField
            fullWidth
            id="annual_fee"
            name="annual_fee"
            label="Annual Fee"
            value={formik.values.annual_fee | ""}
            type="number"
            onChange={formik.handleChange}
            error={
              formik.touched.annual_fee && Boolean(formik.errors.annual_fee)
            }
            required
          />
          <SingleSelectType
            options={
              // staticFields.annual_fee_period &&
              // staticFields.annual_fee_period
              [
                { id: 1, annual_fee_period: "Monthly" },
                { id: 2, annual_fee_period: "Quarterly" },
                { id: 3, annual_fee_period: "Annually" },
              ].map((period) => period.annual_fee_period)
            }
            selected={selectedOptions}
            setSelected={setSelectedOptions}
            label="Annual Fee Period"
            param={"annual_fee_period"}
          />
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label="Annual fee start date"
              id="annual_fee_start_date"
              value={selectedOptions.annual_fee_start_date}
              onChange={(e) =>
                setSelectedOptions({
                  ...selectedOptions,
                  annual_fee_start_date: e,
                })
              }
              renderInput={(params) => <TextField {...params} />}
              required
            />
          </LocalizationProvider>
          <Divider sx={{ my: 1 }} />

          <TextField
            fullWidth
            id="minimum_payout_amount"
            name="minimum_payout_amount"
            label="Minimum Payout Amount"
            value={formik.values.minimum_payout_amount | ""}
            type="number"
            onChange={formik.handleChange}
            error={
              formik.touched.minimum_payout_amount &&
              Boolean(formik.errors.minimum_payout_amount)
            }
          />

          <TextField
            fullWidth
            id="fixed_amount"
            name="fixed_amount"
            label="One-time Fixed Charge"
            value={formik.values.fixed_amount | ""}
            type="number"
            onChange={formik.handleChange}
            error={
              formik.touched.fixed_amount &&
              Boolean(formik.errors.fixed_amount)
            }
          />
          <TextField
            fullWidth
            id="rolling_amount"
            name="rolling_amount"
            label="Rolling Amount"
            value={formik.values.rolling_amount | ""}
            type="number"
            onChange={formik.handleChange}
            error={
              formik.touched.rolling_amount &&
              Boolean(formik.errors.rolling_amount)
            }
          />
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1">Bank Details</Typography>
          {bank_details.map((field, index) => {
            return field.type !== "divider" ? (
              <TextField
                fullWidth
                id={field.name}
                name={field.name}
                label={startCase(field.name) + (field.required && "*")}
                type={field.type ? field.type : "string"}
                value={
                  formik.values[field.name] ? formik.values[field.name] : ""
                }
                onChange={formik.handleChange}
                error={
                  formik.touched[field.name] &&
                  Boolean(formik.errors[field.name])
                }
                // required={field.required}
                key={field.name}
                sx={field.sx || {}}
              />
            ) : (
              <Divider key={index} sx={{ my: 1 }} />
            );
          })}
        </Box>
      </form>
    </FormDialog>
  );
}

export default EditPayoutDialog;
